/**
 * What the site tells Google Tag Manager, stated once.
 *
 * A tag manager is only as good as the vocabulary underneath it: a trigger in
 * the GTM interface matches an event *name*, and a name that is typed into a
 * component is a name that drifts the first time someone renames a button. So
 * every event this site can push is a member of the union below, and every
 * push goes through `track()`. Renaming an event here is a type error at each
 * call site, and `docs/analytics.md` is the same list written out for whoever
 * builds the triggers.
 *
 * Parameter names are snake_case because that is what GA4 stores; the union
 * keys are the data-layer variables you create in GTM.
 */

/** The container, when the deployment has one. Empty means analytics is off. */
export const GTM_ID: string = process.env.NEXT_PUBLIC_GTM_ID ?? ''

/** Where a call to action sat, so two identical labels stay distinguishable. */
export type CtaLocation = 'header' | 'footer' | 'menu' | 'band' | 'hero' | 'card'

export type AnalyticsEvent =
    /* Navigation and outbound. Pushed by the delegated listener. */
    | { event: 'cta_click'; cta_label: string; cta_location: CtaLocation; cta_destination: string }
    | { event: 'outbound_click'; link_url: string; link_domain: string; link_text: string }
    | { event: 'contact_click'; contact_method: 'email' | 'phone'; contact_value: string }
    /* The contact form, which is the only conversion this site has. */
    | { event: 'form_start'; form_name: string }
    | { event: 'form_submit'; form_name: string }
    | { event: 'generate_lead'; form_name: string }
    | {
          event: 'form_error'
          form_name: string
          error_type: 'validation' | 'rate_limit' | 'server' | 'network'
          error_field?: string
      }
    /* Reading behaviour: what a visitor opened before they wrote. */
    | { event: 'faq_open'; faq_question: string }
    | { event: 'service_open'; service_slug: string }
    | { event: 'case_slider_move'; slider_direction: 'next' | 'previous' }
    | { event: 'stack_filter'; filter_type: 'purpose' | 'order'; filter_value: string }
    | { event: 'blog_filter'; filter_value: string }
    /* The banner's answer, so consent itself is measurable. */
    | {
          event: 'consent_update'
          consent_action: 'accept_all' | 'necessary_only' | 'save_choice'
          consent_analytics: boolean
          consent_marketing: boolean
      }

declare global {
    interface Window {
        dataLayer?: unknown[]
        // Defined by the inline script in the layout, in its classic form:
        // `function gtag(){dataLayer.push(arguments)}`. Consent commands are
        // read off the `arguments` object, so calling that function is not the
        // same as pushing an array that looks like it.
        gtag?: (...args: unknown[]) => void
    }
}

/**
 * Push one event, or do nothing at all.
 *
 * Nothing at all is the common case in development and on a preview
 * deployment: no container id means no `dataLayer` worth writing to, and an
 * event queued for a container that never loads is a memory leak with a
 * pleasant name. Server rendering takes the same exit — there is no window to
 * push to, and this runs in components that render on both sides.
 */
export function track(payload: AnalyticsEvent): void {
    if (GTM_ID === '' || typeof window === 'undefined') {
        return
    }

    window.dataLayer = window.dataLayer ?? []
    window.dataLayer.push(payload)
}
